import React, { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InboxIcon from '@mui/icons-material/Inbox';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScaleIcon from '@mui/icons-material/Scale';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useBird, useBirdBlocks, useBirds } from '../hooks/useBirds';
import { weightSamplesService } from '../services/apiServices';
import {
  useDeleteWeek,
  useExtractImage,
  useRecordSamples,
  useRegroupBlock,
  useUpdateSample,
  useWeekStats,
  useWeightTrend,
} from '../hooks/useWeightSamples';

// ── Design tokens ──────────────────────────────────────────────────────────────

const ACCENT = '#ff9800';
const ACCENT_SOFT = 'rgba(255,152,0,0.12)';
const ACCENT_BORDER = 'rgba(255,152,0,0.2)';
const CARD_BG = 'linear-gradient(135deg, #1a1a1a 0%, #1e1e1e 100%)';

const CHART_MEAN = '#38bdf8';
const CHART_UNIF = '#c084fc';

/** Reintegration readiness for an isolated block vs the main flock */
function reintegrationReadiness(
  isolatedStats: { mean_g?: number; uniformity_pct?: number } | null | undefined,
  mainMeanG: number | null | undefined
): 'ready' | 'borderline' | 'not_ready' | null {
  if (!isolatedStats?.mean_g || !mainMeanG) return null;
  const gapPct = ((mainMeanG - isolatedStats.mean_g) / mainMeanG) * 100;
  const unifOk = (isolatedStats.uniformity_pct ?? 0) >= 75;
  const gapOk = gapPct <= 10;
  if (gapOk && unifOk) return 'ready';
  if (gapPct <= 15 || unifOk) return 'borderline';
  return 'not_ready';
}

/** Map backend Excel palette → dark-mode cell styles */
function cellStyle(colour: string | null): {
  bgcolor?: string;
  color?: string;
  fontWeight?: number;
} {
  if (!colour) return {};
  const map: Record<string, { bgcolor: string; color: string }> = {
    '#C6EFCE': { bgcolor: 'rgba(76,175,80,0.22)', color: '#81c784' },
    '#E2F0D9': { bgcolor: 'rgba(76,175,80,0.13)', color: '#a5d6a7' },
    '#FFEB9C': { bgcolor: 'rgba(255,152,0,0.22)', color: '#ffb74d' },
    '#FFC7CE': { bgcolor: 'rgba(239,83,80,0.22)', color: '#ef9a9a' },
    '#BDD7EE': { bgcolor: 'rgba(33,150,243,0.22)', color: '#90caf9' },
  };
  return { ...(map[colour] ?? {}), fontWeight: 700 };
}

/** Common table cell sx */
const TC_BASE = {
  borderBottom: '1px solid rgba(255,152,0,0.06)',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  py: 1,
};

const LABEL_SX = {
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.6px',
  fontWeight: 600,
  color: `${ACCENT}cc`,
};

// ── Custom chart tooltip ───────────────────────────────────────────────────────

const DarkTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: '#1a1a1a',
        border: `1px solid ${ACCENT_BORDER}`,
        borderRadius: 1.5,
        px: 1.75,
        py: 1.25,
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        minWidth: 140,
      }}
    >
      <Typography sx={{ ...LABEL_SX, mb: 0.75, display: 'block' }}>{label}</Typography>
      {payload.map((p: any) => (
        <Box key={p.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', flex: 1 }}>
            {p.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#fff', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {p.value != null ? `${p.value}${p.unit ?? ''}` : '—'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── Colour-coded table cells ───────────────────────────────────────────────────

const ColourCell: React.FC<{ colour: string | null; value: string }> = ({ colour, value }) => (
  <TableCell align="center" sx={{ ...TC_BASE, ...cellStyle(colour) }}>
    {value}
  </TableCell>
);

const GainCell: React.FC<{ gain: any }> = ({ gain }) => {
  if (!gain)
    return (
      <TableCell align="center" sx={TC_BASE}>
        —
      </TableCell>
    );
  const up = gain.actual_gain_g >= 0;
  return (
    <TableCell align="center" sx={TC_BASE}>
      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
        {up ? (
          <TrendingUpIcon sx={{ fontSize: 14, color: '#81c784' }} />
        ) : (
          <TrendingDownIcon sx={{ fontSize: 14, color: '#ef9a9a' }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: up ? '#81c784' : '#ef9a9a',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {up ? '+' : ''}
          {gain.actual_gain_g} g
        </Typography>
        {gain.population_changed && (
          <Tooltip title="Isolation boundary — population changed this week. Gain not like-for-like.">
            <WarningAmberIcon sx={{ fontSize: 13, color: '#ff9800' }} />
          </Tooltip>
        )}
      </Box>
    </TableCell>
  );
};

// ── Record Weights dialog ──────────────────────────────────────────────────────

function RecordWeightsDialog({
  birdId,
  open,
  onClose,
  defaultWeek,
}: {
  birdId: number;
  open: boolean;
  onClose: () => void;
  defaultWeek?: number;
}) {
  const { data: blocks = [] } = useBirdBlocks(birdId);
  const recordSamples = useRecordSamples(birdId);
  const extractImage = useExtractImage(birdId);
  const fileRef = useRef<HTMLInputElement>(null);

  type Reading = {
    objectUrl: string;
    file: File;
    claudeWeight: number | null;
    corrected: string;
    note: string;
    reanalyzing: boolean;
  };

  const [blockId, setBlockId] = useState('');
  const [week, setWeek] = useState<number>(defaultWeek ?? 1);
  const [group, setGroup] = useState<'main' | 'isolated'>('main');
  const [readings, setReadings] = useState<Reading[]>([]);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [draggingOver, setDraggingOver] = useState(false);

  const reset = () => {
    readings.forEach((r) => URL.revokeObjectURL(r.objectUrl));
    setBlockId('');
    setWeek(defaultWeek ?? 1);
    setGroup('main');
    setReadings([]);
    setScanning(false);
    setScanProgress({ done: 0, total: 0 });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const finalWeights = readings
    .map((r) => {
      const v = parseInt(r.corrected, 10);
      return !isNaN(v) && v >= 10 && v <= 5000 ? v : null;
    })
    .filter((v): v is number => v !== null);

  const previewMean =
    finalWeights.length > 0
      ? Math.round(finalWeights.reduce((a, b) => a + b, 0) / finalWeights.length)
      : null;

  const processFiles = async (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    setScanning(true);
    setScanProgress((p) => ({ done: p.done, total: p.total + imageFiles.length }));
    for (const file of imageFiles) {
      const objectUrl = URL.createObjectURL(file);
      const fd = new FormData();
      fd.append('image', file);
      fd.append('week', String(week));
      let claudeWeight: number | null = null;
      let note = '';
      try {
        const result = await extractImage.mutateAsync(fd);
        claudeWeight = result.weight_g;
        note = result.note ?? '';
      } catch {
        note = 'Request failed';
      }
      setReadings((prev) => [
        ...prev,
        {
          objectUrl,
          file,
          claudeWeight,
          corrected: claudeWeight !== null ? String(claudeWeight) : '',
          note,
          reanalyzing: false,
        },
      ]);
      setScanProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setScanning(false);
  };

  const handleImagesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setScanProgress({ done: 0, total: 0 });
    await processFiles(files);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingOver(false);
    setScanProgress({ done: 0, total: 0 });
    await processFiles(Array.from(e.dataTransfer.files));
  };

  const updateCorrected = (idx: number, value: string) =>
    setReadings((prev) => prev.map((r, i) => (i === idx ? { ...r, corrected: value } : r)));

  const removeReading = (idx: number) => {
    URL.revokeObjectURL(readings[idx].objectUrl);
    setReadings((prev) => prev.filter((_, i) => i !== idx));
  };

  const reanalyze = async (idx: number) => {
    const r = readings[idx];
    const correctedVal = parseInt(r.corrected, 10);
    if (isNaN(correctedVal) || correctedVal < 10 || correctedVal > 5000) return;
    setReadings((prev) => prev.map((rd, i) => (i === idx ? { ...rd, reanalyzing: true } : rd)));
    const fd = new FormData();
    fd.append('image', r.file);
    fd.append('week', String(week));
    fd.append('corrected_weight_g', String(correctedVal));
    try {
      const result = await extractImage.mutateAsync(fd);
      setReadings((prev) =>
        prev.map((rd, i) =>
          i === idx ? { ...rd, note: result.note ?? '', reanalyzing: false } : rd
        )
      );
    } catch {
      setReadings((prev) =>
        prev.map((rd, i) =>
          i === idx ? { ...rd, note: 'Re-analysis failed', reanalyzing: false } : rd
        )
      );
    }
  };

  const handleSave = async () => {
    if (!blockId || !week || finalWeights.length === 0) return;
    const validReadings = readings.filter((r) => {
      const v = parseInt(r.corrected, 10);
      return !isNaN(v) && v >= 10 && v <= 5000;
    });
    await recordSamples.mutateAsync({
      block_id: Number(blockId),
      week,
      group,
      weights: validReadings.map((r) => parseInt(r.corrected, 10)),
    });
    handleClose();
  };

  const canSave =
    blockId && week && finalWeights.length > 0 && !recordSamples.isPending && !scanning;

  const DIALOG_SX = {
    '& .MuiDialog-paper': {
      background: '#1a1a1a',
      border: `1px solid ${ACCENT_BORDER}`,
      borderRadius: 2,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => reason !== 'backdropClick' && handleClose()}
      maxWidth="md"
      fullWidth
      sx={DIALOG_SX}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${ACCENT_SOFT}, transparent)`,
          borderBottom: `1px solid ${ACCENT_BORDER}`,
          color: ACCENT,
          fontWeight: 800,
        }}
      >
        Record Weights
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: ACCENT_BORDER }}>
        <Box display="flex" flexWrap="wrap" gap={1.5} mb={2} alignItems="center">
          <FormControl size="small" sx={{ flex: '2 1 160px' }}>
            <InputLabel>Block</InputLabel>
            <Select label="Block" value={blockId} onChange={(e) => setBlockId(e.target.value)}>
              {blocks.map((b: any) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: '1 1 100px' }}>
            <InputLabel>Week</InputLabel>
            <Select label="Week" value={week} onChange={(e) => setWeek(Number(e.target.value))}>
              {Array.from({ length: 19 }, (_, i) => i + 1).map((w) => (
                <MenuItem key={w} value={w}>
                  Week {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={group}
            onChange={(_, v) => v && setGroup(v)}
            sx={{ height: 40 }}
          >
            <ToggleButton value="main">Main</ToggleButton>
            <ToggleButton value="isolated">Isolated</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          mb={2}
          onDragOver={(e) => {
            e.preventDefault();
            setDraggingOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setDraggingOver(false);
          }}
          onDrop={handleDrop}
          onClick={() => !scanning && fileRef.current?.click()}
          sx={{
            border: `2px dashed ${draggingOver ? ACCENT : ACCENT_BORDER}`,
            borderRadius: 2,
            p: 2.5,
            textAlign: 'center',
            bgcolor: draggingOver ? ACCENT_SOFT : 'transparent',
            transition: 'border-color 0.15s, background-color 0.15s',
            cursor: scanning ? 'default' : 'pointer',
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleImagesSelected}
          />
          <UploadFileIcon sx={{ fontSize: 36, color: ACCENT_BORDER, mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {scanning
              ? `Reading ${scanProgress.done} / ${scanProgress.total}…`
              : 'Drop photos here or click to select'}
          </Typography>
          {scanning && (
            <LinearProgress
              sx={{ mt: 1.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }}
            />
          )}
          {finalWeights.length > 0 && !scanning && (
            <Box display="flex" gap={1} justifyContent="center" mt={1}>
              <Chip
                label={`${finalWeights.length} birds`}
                size="small"
                sx={{ bgcolor: ACCENT_SOFT, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}
              />
              {previewMean !== null && (
                <Chip
                  label={`mean ${previewMean} g`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: ACCENT_BORDER, color: 'text.secondary' }}
                />
              )}
            </Box>
          )}
        </Box>

        {readings.length > 0 && (
          <Box
            sx={{
              maxHeight: 420,
              overflowY: 'auto',
              border: `1px solid ${ACCENT_BORDER}`,
              borderRadius: 1.5,
            }}
          >
            {readings.map((r, i) => (
              <Box
                key={i}
                display="flex"
                alignItems="center"
                gap={1.5}
                px={1.5}
                py={1}
                sx={{
                  borderBottom: i < readings.length - 1 ? `1px solid rgba(255,152,0,0.06)` : 'none',
                  bgcolor: r.note && !r.claudeWeight ? 'rgba(239,83,80,0.06)' : 'transparent',
                }}
              >
                <Box
                  component="img"
                  src={r.objectUrl}
                  alt={`Bird ${i + 1}`}
                  onClick={() => setLightboxUrl(r.objectUrl)}
                  sx={{
                    width: 68,
                    height: 68,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                    cursor: 'zoom-in',
                    border: `1px solid ${ACCENT_BORDER}`,
                    '&:hover': { opacity: 0.85 },
                  }}
                />
                <Box flex={1} minWidth={0}>
                  <Typography sx={{ ...LABEL_SX, display: 'block', mb: 0.25 }}>
                    Claude read
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {r.claudeWeight !== null ? `${r.claudeWeight} g` : '—'}
                  </Typography>
                  {r.note && (
                    <Typography variant="caption" color="warning.main" display="block" noWrap>
                      {r.note}
                    </Typography>
                  )}
                </Box>
                <TextField
                  size="small"
                  label="Weight (g)"
                  type="number"
                  value={r.corrected}
                  onChange={(e) => updateCorrected(i, e.target.value)}
                  sx={{ width: 110 }}
                  slotProps={{ htmlInput: { min: 10, max: 5000, step: 5 } }}
                  error={
                    r.corrected !== '' &&
                    (parseInt(r.corrected, 10) < 10 || parseInt(r.corrected, 10) > 5000)
                  }
                />
                <Tooltip title="Re-analyse with corrected weight">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => reanalyze(i)}
                      disabled={
                        r.reanalyzing ||
                        r.corrected === '' ||
                        parseInt(r.corrected, 10) === r.claudeWeight
                      }
                    >
                      {r.reanalyzing ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton size="small" color="error" onClick={() => removeReading(i)}>
                  <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <Dialog open={!!lightboxUrl} onClose={() => setLightboxUrl(null)} maxWidth="md">
        <Box
          component="img"
          src={lightboxUrl ?? ''}
          alt="Scale reading"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Dialog>

      <DialogActions sx={{ borderTop: `1px solid ${ACCENT_BORDER}`, px: 2.5, py: 1.5 }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={handleSave}
          sx={{
            background: `linear-gradient(135deg, ${ACCENT}, #ffa726)`,
            color: '#000',
            fontWeight: 700,
            '&:hover': { background: `linear-gradient(135deg, #f57c00, ${ACCENT})` },
          }}
        >
          {recordSamples.isPending ? (
            <CircularProgress size={16} sx={{ color: '#000' }} />
          ) : (
            `Save ${finalWeights.length > 0 ? finalWeights.length + ' weights' : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Block/week drill-down dialog ───────────────────────────────────────────────

function BlockWeekDialog({
  birdId,
  week,
  blockId,
  blockName,
  open,
  onClose,
}: {
  birdId: number;
  week: number;
  blockId: number;
  blockName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useWeekStats(birdId, week, open);
  const updateSample = useUpdateSample(birdId);
  const [editing, setEditing] = useState<Record<number, string>>({});
  const [lightbox, setLightbox] = useState<string | null>(null);

  const samples: any[] = (data?.samples ?? []).filter((s: any) => s.block_id === blockId);

  const handleSave = (sampleId: number) => {
    const g = parseInt(editing[sampleId], 10);
    if (!g || g < 10 || g > 5000) return;
    updateSample.mutate(
      { sampleId, weightG: g },
      {
        onSuccess: () =>
          setEditing((prev) => {
            const n = { ...prev };
            delete n[sampleId];
            return n;
          }),
      }
    );
  };

  const DIALOG_SX = {
    '& .MuiDialog-paper': {
      background: '#1a1a1a',
      border: `1px solid ${ACCENT_BORDER}`,
      borderRadius: 2,
    },
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" sx={DIALOG_SX}>
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${ACCENT_SOFT}, transparent)`,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
          }}
        >
          <Typography fontWeight={800} sx={{ color: ACCENT }}>
            {blockName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Week {week} · {samples.length} weight{samples.length !== 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: ACCENT_BORDER }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : samples.length === 0 ? (
            <Alert severity="info">No samples recorded for this block.</Alert>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1.5}>
              {samples.map((s: any) => (
                <Paper
                  key={s.id}
                  variant="outlined"
                  sx={{
                    width: { xs: 'calc(50% - 6px)', sm: 'calc(33.33% - 8px)' },
                    overflow: 'hidden',
                    borderRadius: 2,
                    bgcolor: '#222',
                    borderColor: ACCENT_BORDER,
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: ACCENT },
                  }}
                >
                  {s.image_url ? (
                    <Box
                      component="img"
                      src={s.image_url}
                      alt="scale"
                      onClick={() => setLightbox(s.image_url)}
                      sx={{
                        width: '100%',
                        aspectRatio: '4/3',
                        objectFit: 'cover',
                        display: 'block',
                        cursor: 'zoom-in',
                        '&:hover': { opacity: 0.88 },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4/3',
                        bgcolor: 'rgba(255,152,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: `1px solid ${ACCENT_BORDER}`,
                      }}
                    >
                      <Typography variant="caption" color="text.disabled">
                        No image
                      </Typography>
                    </Box>
                  )}
                  <Box px={1.5} py={1}>
                    {editing[s.id] !== undefined ? (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <TextField
                          size="small"
                          type="number"
                          value={editing[s.id]}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, [s.id]: e.target.value }))
                          }
                          slotProps={{ htmlInput: { min: 10, max: 5000, step: 5 } }}
                          sx={{ flex: 1 }}
                          autoFocus
                          disabled={updateSample.isPending}
                        />
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleSave(s.id)}
                              disabled={updateSample.isPending}
                            >
                              {updateSample.isPending ? (
                                <CircularProgress size={14} />
                              ) : (
                                <span style={{ fontSize: 14 }}>✓</span>
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setEditing((prev) => {
                                const n = { ...prev };
                                delete n[s.id];
                                return n;
                              })
                            }
                          >
                            <span style={{ fontSize: 14 }}>✕</span>
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          lineHeight={1.2}
                          sx={{ fontVariantNumeric: 'tabular-nums', color: '#f5f5f5' }}
                        >
                          {(s.weight_g / 1000).toFixed(3)}{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            kg
                          </Typography>
                        </Typography>
                        <Tooltip title="Edit weight">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setEditing((prev) => ({ ...prev, [s.id]: String(s.weight_g) }))
                            }
                          >
                            <EditIcon fontSize="small" sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    {s.note && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.25}
                        noWrap
                      >
                        {s.note}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${ACCENT_BORDER}` }}>
          <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="lg">
        <Box
          component="img"
          src={lightbox ?? ''}
          alt="scale"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Dialog>
    </>
  );
}

// ── Bird selector (header switcher) ───────────────────────────────────────────

function BirdSelect({
  currentId,
  onSelect,
}: {
  currentId: number;
  onSelect: (id: number) => void;
}) {
  const { data: birdsPage, isLoading } = useBirds({});
  const birds = birdsPage?.data ?? [];

  return (
    <Select
      value={currentId}
      onChange={(e) => onSelect(Number(e.target.value))}
      size="small"
      disabled={isLoading}
      sx={{
        fontWeight: 800,
        fontSize: 20,
        color: '#f5f5f5',
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': { py: 0, pl: 0, pr: 3 },
        '& .MuiSelect-icon': { color: `${ACCENT}99`, right: 0 },
        '&:hover .MuiSelect-icon': { color: ACCENT },
      }}
    >
      {birds.map((b: any) => (
        <MenuItem key={b.id} value={b.id} sx={{ fontWeight: b.id === currentId ? 700 : 400 }}>
          {b.name}
        </MenuItem>
      ))}
    </Select>
  );
}

// ── Batch picker ───────────────────────────────────────────────────────────────

function BatchPicker() {
  const navigate = useNavigate();
  const { data: birdsPage, isLoading } = useBirds({});
  const birds = birdsPage?.data ?? [];
  const [selected, setSelected] = useState('');

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          p: 4,
          background: CARD_BG,
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT_SOFT}, rgba(255,152,0,0.06))`,
              border: `1px solid ${ACCENT_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ScaleIcon sx={{ color: ACCENT, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              Flock Weight Trend
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select a rearing batch to analyse
            </Typography>
          </Box>
        </Box>

        <FormControl fullWidth size="small" disabled={isLoading}>
          <InputLabel>Batch</InputLabel>
          <Select
            label="Batch"
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              navigate(`/weight-trend/${e.target.value}`);
            }}
          >
            {isLoading ? (
              <MenuItem disabled>Loading…</MenuItem>
            ) : birds.length === 0 ? (
              <MenuItem disabled>No rearing batches found</MenuItem>
            ) : (
              birds.map((b: any) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

// ── Stat card ──────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  colour,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  colour?: string | null;
  icon?: React.ReactNode;
}) {
  const cs = cellStyle(colour ?? null);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        background: CARD_BG,
        border: `1px solid ${cs.color ? `${cs.color}40` : ACCENT_BORDER}`,
        borderRadius: 2,
        bgcolor: cs.bgcolor ?? undefined,
        flex: '1 1 160px',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{ ...LABEL_SX, color: cs.color ?? `${ACCENT}cc`, display: 'block', mb: 0.75 }}
      >
        {label}
      </Typography>
      <Box display="flex" alignItems="flex-end" gap={1}>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            lineHeight: 1,
            color: cs.color ?? '#f5f5f5',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
        {icon && <Box sx={{ color: cs.color ?? 'text.secondary', mb: 0.1 }}>{icon}</Box>}
      </Box>
      {sub && (
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.25, display: 'block' }}
        >
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function FlockWeightTrend() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const birdId = Number(id);
  const { data: bird } = useBird(birdId, !!birdId);
  const { data: trendData, isLoading, error } = useWeightTrend(birdId, !!birdId);
  const deleteWeek = useDeleteWeek(birdId);
  const regroupBlock = useRegroupBlock(birdId);

  const [recordOpen, setRecordOpen] = useState(false);
  const [drillDown, setDrillDown] = useState<{
    week: number;
    blockId: number;
    blockName: string;
  } | null>(null);
  const [downloadingWeek, setDownloadingWeek] = useState<number | null>(null);

  const exportWeekCsv = async (week: number) => {
    setDownloadingWeek(week);
    try {
      const data = await weightSamplesService.getWeekStats(birdId, week);
      const samples: any[] = data.samples ?? [];
      const batchName = bird?.name ?? `batch-${birdId}`;
      const rows = [
        ['Weight (g)', 'Weight (kg)'],
        ...samples.map((s: any) => [s.weight_g, (s.weight_g / 1000).toFixed(3)]),
      ];
      const csv = rows
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchName.replace(/\s+/g, '_')}_week${week}_weights.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingWeek(null);
    }
  };

  const weeks = useMemo(() => [...(trendData?.weeks ?? [])].reverse(), [trendData]);

  const blockNames = useMemo(() => {
    const seen = new Set<string>();
    (trendData?.weeks ?? []).forEach((w: any) =>
      (w.by_block ?? []).forEach((b: any) => seen.add(b.block_name))
    );
    return Array.from(seen);
  }, [trendData]);

  const chartData = useMemo(() => {
    if (!trendData?.weeks) return [];
    return [...trendData.weeks].map((w: any) => ({
      week: `Wk ${w.week}`,
      day: `Day ${w.week * 7}`,
      mean: w.stats?.mean_g != null ? Math.round(w.stats.mean_g) : null,
      standard: trendData.standard?.[w.week] ?? null,
      uniformity:
        w.stats?.uniformity_pct != null ? Math.round(w.stats.uniformity_pct * 10) / 10 : null,
    }));
  }, [trendData]);

  if (!id) return <BatchPicker />;

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={320}
        gap={2}
      >
        <CircularProgress sx={{ color: ACCENT }} />
        <Typography variant="body2" color="text.secondary">
          Loading weight trend…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load weight trend data.</Alert>
      </Box>
    );
  }

  const hasData = weeks.length > 0;
  const breedLabel =
    trendData?.breed === 'lohmann_white' ? 'LSL-Classic · White' : 'Brown-Classic · Brown';
  const latestWeek: any = weeks[0];

  const PAPER_SX = {
    elevation: 0,
    sx: {
      background: CARD_BG,
      border: `1px solid ${ACCENT_BORDER}`,
      borderRadius: 2,
    },
  };

  const TH_SX = {
    ...TC_BASE,
    ...LABEL_SX,
    py: 1.25,
    bgcolor: 'rgba(255,152,0,0.06)',
    borderBottom: `1px solid ${ACCENT_BORDER}`,
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
          pb: 2.5,
          borderBottom: `1px solid ${ACCENT_BORDER}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            size="small"
            onClick={() => navigate('/weight-trend')}
            sx={{ border: `1px solid ${ACCENT_BORDER}`, '&:hover': { bgcolor: ACCENT_SOFT } }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <BirdSelect currentId={birdId} onSelect={(id) => navigate(`/weight-trend/${id}`)} />
              {trendData?.breed && (
                <Chip
                  label={breedLabel}
                  size="small"
                  sx={{
                    bgcolor: ACCENT_SOFT,
                    color: ACCENT,
                    border: `1px solid ${ACCENT_BORDER}`,
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              )}
              {bird?.age_weeks && (
                <Chip
                  label={`Wk ${bird.age_weeks}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 11 }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Flock weight analysis · {weeks.length} week{weeks.length !== 1 ? 's' : ''} of data
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InboxIcon />}
            onClick={() => navigate('/weight-submissions')}
            sx={{
              borderColor: ACCENT_BORDER,
              color: 'text.secondary',
              '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: ACCENT_SOFT },
            }}
          >
            Submissions
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRecordOpen(true)}
            sx={{
              background: `linear-gradient(135deg, ${ACCENT}, #ffa726)`,
              color: '#000',
              fontWeight: 700,
              '&:hover': { background: `linear-gradient(135deg, #f57c00, ${ACCENT})` },
            }}
          >
            Record Weights
          </Button>
        </Box>
      </Box>

      {/* ── Empty state ──────────────────────────────────────────────── */}
      {!hasData && (
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            background: CARD_BG,
            border: `1px dashed ${ACCENT_BORDER}`,
            borderRadius: 3,
          }}
        >
          <ScaleIcon sx={{ fontSize: 52, color: ACCENT_BORDER, mb: 2 }} />
          <Typography variant="h6" fontWeight={700} mb={0.5}>
            No weight data yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Record the first week&apos;s weights to start tracking this flock&apos;s growth.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRecordOpen(true)}
            sx={{
              background: `linear-gradient(135deg, ${ACCENT}, #ffa726)`,
              color: '#000',
              fontWeight: 700,
            }}
          >
            Record Weights
          </Button>
        </Box>
      )}

      {hasData && (
        <>
          {/* ── KPI strip ───────────────────────────────────────────── */}
          {latestWeek && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              <StatCard
                label={`Mean Weight · Wk ${latestWeek.week}`}
                value={
                  latestWeek.stats?.mean_g != null
                    ? `${Math.round(latestWeek.stats.mean_g)} g`
                    : '—'
                }
                sub={
                  latestWeek.stats?.standard_g != null
                    ? `standard ${latestWeek.stats.standard_g} g`
                    : undefined
                }
                colour={latestWeek.deviation_colour}
                icon={<ScaleIcon sx={{ fontSize: 18 }} />}
              />
              <StatCard
                label="Deviation"
                value={
                  latestWeek.stats?.deviation_pct != null
                    ? `${latestWeek.stats.deviation_pct >= 0 ? '+' : ''}${latestWeek.stats.deviation_pct.toFixed(1)}%`
                    : '—'
                }
                colour={latestWeek.deviation_colour}
                icon={
                  latestWeek.stats?.deviation_pct != null ? (
                    latestWeek.stats.deviation_pct >= 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 18 }} />
                    )
                  ) : undefined
                }
              />
              <StatCard
                label="Uniformity"
                value={
                  latestWeek.stats?.uniformity_pct != null
                    ? `${latestWeek.stats.uniformity_pct.toFixed(1)}%`
                    : '—'
                }
                colour={latestWeek.uniformity_colour}
              />
              <StatCard
                label="Sample Size"
                value={latestWeek.stats?.n ?? '—'}
                sub={`CV ${latestWeek.stats?.cv_pct != null ? latestWeek.stats.cv_pct.toFixed(1) + '%' : '—'}`}
              />
              {latestWeek.isolated_n > 0 && (
                <StatCard
                  label="Isolated"
                  value={latestWeek.isolated_n}
                  sub="birds separated"
                  colour="#FFEB9C"
                />
              )}
            </Box>
          )}

          {/* ── Charts ──────────────────────────────────────────────── */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Mean weight vs standard */}
            <Paper {...PAPER_SX}>
              <Box px={3} pt={2.5} pb={1}>
                <Typography fontWeight={800} sx={{ fontSize: 15 }}>
                  Mean Weight vs Breed Standard
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Grams · main group only
                </Typography>
              </Box>
              <Box px={2} pb={2.5}>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradMean" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_MEAN} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={CHART_MEAN} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradStd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={ACCENT} stopOpacity={0.12} />
                        <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                      axisLine={false}
                      tickLine={false}
                      unit=" g"
                      width={58}
                    />
                    <ChartTooltip content={<DarkTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="standard"
                      name="Breed Standard"
                      stroke={ACCENT}
                      strokeWidth={1.5}
                      strokeDasharray="5 3"
                      fill="url(#gradStd)"
                      dot={false}
                      unit=" g"
                      connectNulls
                    />
                    <Area
                      type="monotone"
                      dataKey="mean"
                      name="Sample Mean"
                      stroke={CHART_MEAN}
                      strokeWidth={2.5}
                      fill="url(#gradMean)"
                      dot={{ r: 4, fill: CHART_MEAN, strokeWidth: 0 }}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                      unit=" g"
                      connectNulls
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Uniformity */}
            <Paper {...PAPER_SX}>
              <Box px={3} pt={2.5} pb={1}>
                <Typography fontWeight={800} sx={{ fontSize: 15 }}>
                  Flock Uniformity
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  % within ±10% of sample mean
                </Typography>
              </Box>
              <Box px={2} pb={2.5}>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="zoneGreen" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(76,175,80,0.12)" />
                        <stop offset="100%" stopColor="rgba(76,175,80,0.04)" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                      axisLine={false}
                      tickLine={false}
                      unit="%"
                      domain={[60, 100]}
                    />
                    <ChartTooltip content={<DarkTooltip />} />
                    <ReferenceArea y1={90} y2={100} fill="rgba(76,175,80,0.1)" />
                    <ReferenceArea y1={80} y2={90} fill="rgba(139,195,74,0.07)" />
                    <ReferenceArea y1={75} y2={80} fill="rgba(255,152,0,0.07)" />
                    <ReferenceLine
                      y={90}
                      stroke="rgba(76,175,80,0.5)"
                      strokeDasharray="4 2"
                      label={{
                        value: '90%',
                        position: 'right',
                        fontSize: 10,
                        fill: 'rgba(76,175,80,0.7)',
                      }}
                    />
                    <ReferenceLine
                      y={80}
                      stroke="rgba(139,195,74,0.4)"
                      strokeDasharray="4 2"
                      label={{
                        value: '80%',
                        position: 'right',
                        fontSize: 10,
                        fill: 'rgba(139,195,74,0.6)',
                      }}
                    />
                    <ReferenceLine
                      y={75}
                      stroke="rgba(255,152,0,0.5)"
                      strokeDasharray="4 2"
                      label={{
                        value: '75%',
                        position: 'right',
                        fontSize: 10,
                        fill: 'rgba(255,152,0,0.7)',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="uniformity"
                      name="Uniformity"
                      stroke={CHART_UNIF}
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: CHART_UNIF, strokeWidth: 0 }}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                      unit="%"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>

          {/* ── Weights by Block ─────────────────────────────────────── */}
          {blockNames.length > 0 && (
            <Paper {...PAPER_SX} sx={{ ...PAPER_SX.sx, mb: 3, overflow: 'hidden' }}>
              <Box px={3} py={2} borderBottom={`1px solid ${ACCENT_BORDER}`}>
                <Typography fontWeight={800} sx={{ fontSize: 15 }}>
                  Weights by Block
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mean weight (g) per room — click any cell to inspect individual birds
                </Typography>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={TH_SX}>Week</TableCell>
                      {blockNames.map((name) => (
                        <TableCell key={name} align="center" sx={TH_SX}>
                          {name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weeks.map((w: any) => {
                      const blockMap: Record<string, any> = {};
                      (w.by_block ?? []).forEach((b: any) => {
                        blockMap[b.block_name] = b;
                      });
                      return (
                        <TableRow
                          key={w.week}
                          sx={{ '&:hover': { bgcolor: 'rgba(255,152,0,0.03)' } }}
                        >
                          <TableCell
                            sx={{
                              ...TC_BASE,
                              fontWeight: 700,
                              color: ACCENT,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Wk {w.week}
                          </TableCell>
                          {blockNames.map((name) => {
                            const b = blockMap[name];
                            if (!b)
                              return (
                                <TableCell
                                  key={name}
                                  align="center"
                                  sx={{ ...TC_BASE, color: 'rgba(255,255,255,0.2)' }}
                                >
                                  —
                                </TableCell>
                              );
                            const mean =
                              b.stats?.mean_g != null ? Math.round(b.stats.mean_g) : null;
                            const cs = cellStyle(b.deviation_colour);
                            return (
                              <TableCell
                                key={name}
                                align="center"
                                onClick={() =>
                                  setDrillDown({
                                    week: w.week,
                                    blockId: b.block_id,
                                    blockName: name,
                                  })
                                }
                                sx={{
                                  ...TC_BASE,
                                  ...cs,
                                  cursor: 'pointer',
                                  transition: 'filter 0.12s',
                                  '&:hover': { filter: 'brightness(1.25)' },
                                  position: 'relative',
                                }}
                              >
                                {mean != null ? (
                                  <>
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 13,
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                    >
                                      {mean} g
                                    </Typography>
                                    <Typography
                                      display="block"
                                      sx={{
                                        fontSize: 10,
                                        opacity: 0.6,
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                    >
                                      n={b.stats?.n ?? 0}
                                    </Typography>
                                  </>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}

          {/* ── Weekly summary ───────────────────────────────────────── */}
          <Paper {...PAPER_SX} sx={{ ...PAPER_SX.sx, mb: 3, overflow: 'hidden' }}>
            <Box px={3} py={2} borderBottom={`1px solid ${ACCENT_BORDER}`}>
              <Typography fontWeight={800} sx={{ fontSize: 15 }}>
                Weekly Summary
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All metrics · latest week first
              </Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      'Week',
                      'n',
                      'Mean (g)',
                      'Std (g)',
                      'Deviation',
                      'Uniformity',
                      'Weekly Gain',
                      'CV%',
                      'Wk+1 Target',
                      'Isolated',
                      '',
                    ].map((h) => (
                      <TableCell
                        key={h}
                        align={h === 'Week' || h === '' ? 'left' : 'center'}
                        sx={TH_SX}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeks.map((w: any) => (
                    <TableRow key={w.week} sx={{ '&:hover': { bgcolor: 'rgba(255,152,0,0.03)' } }}>
                      <TableCell
                        sx={{ ...TC_BASE, fontWeight: 700, color: ACCENT, whiteSpace: 'nowrap' }}
                      >
                        Wk {w.week}
                      </TableCell>
                      <TableCell align="center" sx={TC_BASE}>
                        {w.stats?.n ?? '—'}
                      </TableCell>
                      <TableCell align="center" sx={TC_BASE}>
                        {w.stats?.mean_g != null ? Math.round(w.stats.mean_g) : '—'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ ...TC_BASE, color: 'rgba(255,255,255,0.45)' }}
                      >
                        {w.stats?.standard_g ?? '—'}
                      </TableCell>
                      <ColourCell
                        colour={w.deviation_colour}
                        value={
                          w.stats?.deviation_pct != null
                            ? `${w.stats.deviation_pct >= 0 ? '+' : ''}${w.stats.deviation_pct.toFixed(1)}%`
                            : '—'
                        }
                      />
                      <ColourCell
                        colour={w.uniformity_colour}
                        value={
                          w.stats?.uniformity_pct != null
                            ? `${w.stats.uniformity_pct.toFixed(1)}%`
                            : '—'
                        }
                      />
                      <GainCell gain={w.weekly_gain} />
                      <TableCell align="center" sx={TC_BASE}>
                        {w.stats?.cv_pct != null ? `${w.stats.cv_pct.toFixed(1)}%` : '—'}
                      </TableCell>
                      {/* Wk+1 Target: this week's actual mean + expected gain to next week */}
                      {(() => {
                        const std = trendData?.standard ?? {};
                        const meanG: number | null = w.stats?.mean_g ?? null;
                        const stdThis: number | null = std[w.week] ?? null;
                        const stdNext: number | null = std[w.week + 1] ?? null;
                        if (meanG == null || stdThis == null || stdNext == null) {
                          return (
                            <TableCell
                              align="center"
                              sx={{ ...TC_BASE, color: 'rgba(255,255,255,0.2)' }}
                            >
                              —
                            </TableCell>
                          );
                        }
                        const expectedGain = stdNext - stdThis;
                        const projection = Math.round(meanG + expectedGain);
                        // Find next week's actual mean if it exists
                        const nextWeekData: any = (trendData?.weeks ?? []).find(
                          (wk: any) => wk.week === w.week + 1
                        );
                        const nextActual: number | null =
                          nextWeekData?.stats?.mean_g != null
                            ? Math.round(nextWeekData.stats.mean_g)
                            : null;
                        const diff = nextActual != null ? nextActual - projection : null;
                        const diffColour =
                          diff == null
                            ? null
                            : diff >= 0
                              ? '#81c784'
                              : diff >= -30
                                ? '#ffb74d'
                                : '#ef9a9a';
                        return (
                          <TableCell align="center" sx={TC_BASE}>
                            <Tooltip
                              arrow
                              title={
                                nextActual != null
                                  ? `Projected ${projection} g · Week ${w.week + 1} actual ${nextActual} g · ${(diff ?? 0) >= 0 ? '+' : ''}${diff} g vs projection`
                                  : `If birds gain the expected ${expectedGain} g, week ${w.week + 1} mean should be ${projection} g`
                              }
                            >
                              <Box
                                display="inline-flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={0.25}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#90caf9',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {projection} g
                                </Typography>
                                {nextActual != null && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: 10,
                                      color: diffColour,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {(diff ?? 0) >= 0 ? '+' : ''}
                                    {diff} g
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })()}
                      <TableCell align="center" sx={TC_BASE}>
                        {w.isolated_n > 0 ? (
                          (() => {
                            // Determine aggregate readiness across all isolated blocks this week
                            const isolatedBlocks = (w.by_block ?? []).filter(
                              (b: any) => b.isolated_n > 0
                            );
                            const readinessList = isolatedBlocks.map((b: any) =>
                              reintegrationReadiness(
                                b.isolated_stats,
                                b.stats?.mean_g ?? w.stats?.mean_g
                              )
                            );
                            const allReady =
                              readinessList.length > 0 &&
                              readinessList.every((r: any) => r === 'ready');
                            const anyReady = readinessList.some(
                              (r: any) => r === 'ready' || r === 'borderline'
                            );
                            return (
                              <Tooltip
                                arrow
                                title={
                                  allReady
                                    ? 'All isolated blocks ready to rejoin'
                                    : anyReady
                                      ? 'Some blocks borderline — expand week for details'
                                      : 'Isolated birds not yet ready to rejoin'
                                }
                              >
                                <Box display="inline-flex" alignItems="center" gap={0.5}>
                                  <Chip
                                    label={w.isolated_n}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(255,152,0,0.2)',
                                      color: '#ffb74d',
                                      border: '1px solid rgba(255,152,0,0.3)',
                                      fontWeight: 700,
                                      height: 20,
                                      fontSize: 11,
                                    }}
                                  />
                                  {allReady && (
                                    <CheckCircleOutlineIcon
                                      sx={{ fontSize: 13, color: '#81c784' }}
                                    />
                                  )}
                                  {!allReady && anyReady && (
                                    <WarningAmberIcon sx={{ fontSize: 13, color: '#ffb74d' }} />
                                  )}
                                </Box>
                              </Tooltip>
                            );
                          })()
                        ) : (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={TC_BASE}>
                        <Box display="flex" gap={0.25}>
                          <Tooltip title="Export to CSV">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => exportWeekCsv(w.week)}
                                disabled={downloadingWeek === w.week}
                                sx={{ '&:hover': { color: ACCENT } }}
                              >
                                {downloadingWeek === w.week ? (
                                  <CircularProgress size={13} />
                                ) : (
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete this week's samples">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                if (window.confirm(`Delete all samples for week ${w.week}?`))
                                  deleteWeek.mutate(w.week);
                              }}
                              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                            >
                              <span style={{ fontSize: 13, lineHeight: 1 }}>✕</span>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* ── Per-week observations, warnings, block breakdown ──────── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {weeks.map((w: any) => {
              const hasObs = !!w.observation;
              const hasWarnings = w.warnings?.length > 0;
              const hasBlocks = w.by_block?.length > 0;
              const hasEquipmentIssue = w.by_block?.some(
                (b: any) =>
                  b.equipment?.feeders_shortfall > 0 ||
                  b.equipment?.drinkers_shortfall > 0 ||
                  (w.week === 10 && b.equipment?.week11_feeders_shortfall > 0)
              );
              if (!hasObs && !hasWarnings && !hasBlocks) return null;

              return (
                <Accordion
                  key={w.week}
                  elevation={0}
                  disableGutters
                  sx={{
                    background: CARD_BG,
                    border: `1px solid ${hasWarnings || hasEquipmentIssue ? 'rgba(255,152,0,0.35)' : ACCENT_BORDER}`,
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { border: `1px solid ${ACCENT}` },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }} />
                    }
                    sx={{
                      px: 2.5,
                      py: 0,
                      minHeight: 48,
                      '& .MuiAccordionSummary-content': { my: 1.25, alignItems: 'center', gap: 1 },
                    }}
                  >
                    <Typography fontWeight={700} sx={{ fontSize: 14 }}>
                      Week {w.week}
                    </Typography>
                    {(hasWarnings || hasEquipmentIssue) && (
                      <WarningAmberIcon sx={{ fontSize: 15, color: ACCENT }} />
                    )}
                    {hasObs && (
                      <Chip
                        label="obs"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          bgcolor: 'rgba(180,83,9,0.2)',
                          color: '#b45309',
                          border: '1px solid rgba(180,83,9,0.3)',
                        }}
                      />
                    )}
                    {hasBlocks && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.3)', ml: 'auto', mr: 1 }}
                      >
                        {w.by_block.length} block{w.by_block.length !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    <Divider sx={{ borderColor: ACCENT_BORDER }} />

                    {hasObs && (
                      <Box px={2.5} py={1.75}>
                        <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                          <AutoAwesomeIcon sx={{ fontSize: 13, color: '#b45309' }} />
                          <Typography sx={{ ...LABEL_SX, color: '#b45309' }}>
                            Field observation
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {w.observation}
                        </Typography>
                      </Box>
                    )}

                    {hasWarnings && (
                      <Box px={2.5} py={1}>
                        {w.warnings.map((warn: string, i: number) => (
                          <Alert
                            key={i}
                            severity="warning"
                            icon={<WarningAmberIcon fontSize="small" />}
                            sx={{
                              mb: 0.75,
                              py: 0.5,
                              fontSize: 13,
                              bgcolor: 'rgba(255,152,0,0.08)',
                              border: `1px solid rgba(255,152,0,0.2)`,
                              '& .MuiAlert-icon': { color: ACCENT },
                            }}
                          >
                            {warn}
                          </Alert>
                        ))}
                      </Box>
                    )}

                    {hasBlocks && (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {[
                                'Block',
                                'Group',
                                'n',
                                'Mean (g)',
                                'Deviation',
                                'Uniformity',
                                'Below Std',
                                '<5% Std',
                                'Isolated',
                                'Equipment',
                              ].map((h) => (
                                <TableCell
                                  key={h}
                                  align={h === 'Block' ? 'left' : 'center'}
                                  sx={{ ...TH_SX, bgcolor: 'rgba(255,152,0,0.04)' }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {w.by_block.map((b: any) => {
                              // For isolated-only blocks b.stats is null; fall back to isolated_stats for display
                              const displayStats = b.stats ?? b.isolated_stats ?? null;
                              const displayDeviationColour = b.stats ? b.deviation_colour : null;
                              const displayUniformityColour = b.stats ? b.uniformity_colour : null;
                              // Use week main mean as fallback so readiness can still be computed
                              const mainMean = b.stats?.mean_g ?? w.stats?.mean_g ?? null;
                              const readiness = reintegrationReadiness(b.isolated_stats, mainMean);
                              const isRegrouping =
                                regroupBlock.isPending &&
                                (regroupBlock.variables as any)?.block_id === b.block_id &&
                                (regroupBlock.variables as any)?.week === w.week;
                              const currentGroup = b.stats != null ? 'main' : 'isolated';

                              return (
                                <TableRow
                                  key={b.block_id}
                                  sx={{ '&:hover': { bgcolor: 'rgba(255,152,0,0.03)' } }}
                                >
                                  <TableCell sx={{ ...TC_BASE, fontWeight: 700 }}>
                                    {b.block_name}
                                  </TableCell>
                                  {/* Group toggle */}
                                  <TableCell align="center" sx={TC_BASE}>
                                    <Tooltip
                                      title={`Move all ${w.week} samples to ${currentGroup === 'main' ? 'isolated' : 'main'} group`}
                                      arrow
                                    >
                                      <span>
                                        <Chip
                                          label={currentGroup}
                                          size="small"
                                          onClick={() => {
                                            if (isRegrouping) return;
                                            const newGroup =
                                              currentGroup === 'main' ? 'isolated' : 'main';
                                            if (
                                              window.confirm(
                                                `Move all week ${w.week} samples for "${b.block_name}" to ${newGroup}?`
                                              )
                                            ) {
                                              regroupBlock.mutate({
                                                block_id: b.block_id,
                                                week: w.week,
                                                group: newGroup,
                                              });
                                            }
                                          }}
                                          icon={
                                            isRegrouping ? (
                                              <CircularProgress
                                                size={10}
                                                sx={{ color: 'inherit !important' }}
                                              />
                                            ) : (
                                              <SwapHorizIcon sx={{ fontSize: '14px !important' }} />
                                            )
                                          }
                                          sx={{
                                            height: 20,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            ...(currentGroup === 'main'
                                              ? {
                                                  bgcolor: 'rgba(33,150,243,0.15)',
                                                  color: '#90caf9',
                                                  border: '1px solid rgba(33,150,243,0.3)',
                                                }
                                              : {
                                                  bgcolor: 'rgba(255,152,0,0.15)',
                                                  color: '#ffb74d',
                                                  border: '1px solid rgba(255,152,0,0.3)',
                                                }),
                                            '&:hover': { filter: 'brightness(1.2)' },
                                          }}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center" sx={TC_BASE}>
                                    {displayStats?.n ?? '—'}
                                  </TableCell>
                                  <TableCell align="center" sx={TC_BASE}>
                                    {displayStats?.mean_g != null
                                      ? Math.round(displayStats.mean_g)
                                      : '—'}
                                  </TableCell>
                                  <ColourCell
                                    colour={displayDeviationColour}
                                    value={
                                      displayStats?.deviation_pct != null
                                        ? `${displayStats.deviation_pct >= 0 ? '+' : ''}${displayStats.deviation_pct.toFixed(1)}%`
                                        : '—'
                                    }
                                  />
                                  <ColourCell
                                    colour={displayUniformityColour}
                                    value={
                                      displayStats?.uniformity_pct != null
                                        ? `${displayStats.uniformity_pct.toFixed(1)}%`
                                        : '—'
                                    }
                                  />
                                  <TableCell align="center" sx={TC_BASE}>
                                    {displayStats?.below_standard ?? '—'}
                                  </TableCell>
                                  <TableCell align="center" sx={TC_BASE}>
                                    {displayStats?.more_than_5_under ?? '—'}
                                  </TableCell>
                                  {/* Isolated birds + readiness */}
                                  <TableCell align="center" sx={TC_BASE}>
                                    {b.isolated_n > 0 ? (
                                      <Tooltip
                                        arrow
                                        title={
                                          b.isolated_stats
                                            ? (() => {
                                                const iso = b.isolated_stats;
                                                const gap = mainMean
                                                  ? (
                                                      ((mainMean - iso.mean_g) / mainMean) *
                                                      100
                                                    ).toFixed(1)
                                                  : null;
                                                return [
                                                  `${b.isolated_n} isolated birds`,
                                                  iso.mean_g != null
                                                    ? `Mean: ${Math.round(iso.mean_g)} g`
                                                    : '',
                                                  iso.uniformity_pct != null
                                                    ? `Uniformity: ${iso.uniformity_pct.toFixed(1)}%`
                                                    : '',
                                                  gap != null ? `Gap vs main: ${gap}%` : '',
                                                  readiness === 'ready'
                                                    ? '✓ Ready to rejoin main group'
                                                    : readiness === 'borderline'
                                                      ? '~ Borderline — monitor another week'
                                                      : 'Keep separate — gap or uniformity not met',
                                                ]
                                                  .filter(Boolean)
                                                  .join(' · ');
                                              })()
                                            : `${b.isolated_n} isolated birds`
                                        }
                                      >
                                        <Box display="inline-flex" alignItems="center" gap={0.5}>
                                          <Chip
                                            label={b.isolated_n}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: 10,
                                              fontWeight: 700,
                                              bgcolor: 'rgba(255,152,0,0.15)',
                                              color: '#ffb74d',
                                              border: '1px solid rgba(255,152,0,0.25)',
                                            }}
                                          />
                                          {readiness === 'ready' && (
                                            <CheckCircleOutlineIcon
                                              sx={{ fontSize: 14, color: '#81c784' }}
                                            />
                                          )}
                                          {readiness === 'borderline' && (
                                            <WarningAmberIcon
                                              sx={{ fontSize: 14, color: '#ffb74d' }}
                                            />
                                          )}
                                          {readiness === 'not_ready' && (
                                            <Typography
                                              sx={{ fontSize: 12, color: '#ef9a9a', lineHeight: 1 }}
                                            >
                                              ✕
                                            </Typography>
                                          )}
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'rgba(255,255,255,0.2)' }}
                                      >
                                        —
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="center" sx={TC_BASE}>
                                    {b.equipment == null ? (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'rgba(255,255,255,0.2)' }}
                                      >
                                        —
                                      </Typography>
                                    ) : b.equipment.feeders_shortfall > 0 ||
                                      b.equipment.drinkers_shortfall > 0 ||
                                      (w.week === 10 &&
                                        b.equipment.week11_feeders_shortfall > 0) ? (
                                      <Tooltip
                                        title={[
                                          b.equipment.feeders_shortfall > 0
                                            ? `Feeders: have ${b.equipment.feeders_required - b.equipment.feeders_shortfall}, need ${b.equipment.feeders_required} (+${b.equipment.feeders_shortfall})`
                                            : '',
                                          b.equipment.drinkers_shortfall > 0
                                            ? `Drinkers: have ${b.equipment.drinkers_required - b.equipment.drinkers_shortfall}, need ${b.equipment.drinkers_required} (+${b.equipment.drinkers_shortfall})`
                                            : '',
                                          w.week === 10 && b.equipment.week11_feeders_shortfall > 0
                                            ? `Week 11 step-up: need ${b.equipment.week11_feeders_shortfall} more feeder${b.equipment.week11_feeders_shortfall !== 1 ? 's' : ''}`
                                            : '',
                                        ]
                                          .filter(Boolean)
                                          .join(' · ')}
                                        arrow
                                      >
                                        <Chip
                                          label="short"
                                          size="small"
                                          sx={{
                                            bgcolor: 'rgba(255,152,0,0.2)',
                                            color: '#ffb74d',
                                            border: '1px solid rgba(255,152,0,0.3)',
                                            fontSize: 10,
                                            height: 20,
                                            fontWeight: 700,
                                          }}
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: '#81c784', fontWeight: 700 }}
                                      >
                                        ✓
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </>
      )}

      <RecordWeightsDialog
        birdId={birdId}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        defaultWeek={bird ? Math.min(Math.max(bird.age_weeks ?? 1, 1), 20) : undefined}
      />

      {drillDown && (
        <BlockWeekDialog
          birdId={birdId}
          week={drillDown.week}
          blockId={drillDown.blockId}
          blockName={drillDown.blockName}
          open={!!drillDown}
          onClose={() => setDrillDown(null)}
        />
      )}
    </Box>
  );
}
